/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { DefaultPalette } from '@fluentui/style-utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisSplitPane } from 'src/core/components/LuisSplitPane';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSplitPane } from 'src/core/hooks/useSplitPane';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { watchResize } from 'src/core/utils/resizeWatcher';
import { routeTable } from 'src/core/utils/routeTable';
import { QnaMakerLeftSidePane } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerLeftSidePane';
import { QnaMakerQnaPairDetails } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairDetails';
import { QnaMakerSynonymGroupDetails } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymGroupDetails';
import { QnaMakerSynonymGroupRightCommandBar } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymGroupRightCommandBar';
import { useQnaAssets } from 'src/qnaMaker/modules/hooks/useQnaAssets';
import { useSynonymsActions } from 'src/qnaMaker/modules/hooks/useSynonymsActions';
import styled from 'styled-components';

const messages = defineMessages({
    loading: {
        id: 'global.loading',
        defaultMessage: 'Loading...'
    },
    pageTitle: {
        id: 'qnaMaker.QnaMakerEditPageV2.pageTitle',
        defaultMessage: 'Edit knowledge base'
    },
    sources: {
        id: 'qnaMaker.QnaMakerEditPageV2.sources',
        defaultMessage: '{unstructuredSources} unstructured sources and {structuredSources} structured sources.'
    },
    viewSources: {
        id: 'qnaMaker.QnaMakerEditPageV2.viewSources',
        defaultMessage: 'View sources'
    },
    questionAnswerPair: {
        id: 'qnaMaker.QnaMakerEditPageV2.questionAnswerPair',
        defaultMessage: 'Question answer pairs'
    },
    unsavedChangesWarningMsg: {
        id: 'qnaMaker.QnaMakerEditPageV2.unsavedChangesWarningMsg',
        defaultMessage: 'You have unsaved changes. Click on Save changes in the command bar to save your recent work.'
    },
    qnaEmptyStateMessage: {
        id: 'qnaMaker.QnaMakerEditPageV2.qnaEmptyStateMessage',
        defaultMessage: "You haven't created any question and answer pairs yet."
    },
    synonymEmptyStateMessage: {
        id: 'qnaMaker.QnaMakerEditPageV2.synonymEmptyStateMessage',
        defaultMessage: "You haven't created any synonym groups yet."
    },
    expandRightSidePane: {
        id: 'qnaMaker.QnaMakerEditPageV2.expandRightSidePane',
        defaultMessage: 'Expand right side pane'
    },
    multiSelectStateMessage: {
        id: 'qnaMaker.QnaMakerEditPageV2.multiSelectStateMessage',
        defaultMessage: 'Select only one pair to view here.'
    }
});

const theme = getScopedTheme('QnaMakerEditPage');

const HeaderContainer = styled(Stack)({
    marginTop: '10px',
    paddingBottom: '4.5px',
    borderBottom: theme.borderColor
});

const Root = styled.div`
    height: 100%;
    position: relative;
    overflow: hidden;
`;

const VerticalSplitPane = styled(LuisSplitPane)`
    .Resizer.vertical {
        border-radius: 5px;

        :hover {
            cursor: col-resize;
            background: ${DefaultPalette.neutralLight};
        }

        :after {
            width: 2px;
            opacity: 0.5;
            border-radius: 5px;
        }
    }

    .Pane2 {
        overflow-y: auto;
    }
`;

/**
 * Renders the edit page for the QnA Maker service.
 */
export const QnaMakerEditPageV2 = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const { currentQnaMakerProjectStore } = rootStore;
    const history = useHistory();
    const {
        sourcesStoreHouse: { items, tracker }
    } = currentQnaMakerProjectStore;

    const { size: width, setSize: setWidth, maxSize: maxWidth, setMaxSize: setMaxWidth } = useSplitPane({
        defaultSize: 400,
        maxSize: window.innerWidth * 0.75
    });

    const rootElementRef = React.useRef<HTMLDivElement>();

    const searchParams = new URLSearchParams(location.search);
    const activeTab = decodeURIComponent(searchParams.get('assets'));

    const { qnaUiStore, synonymUiStore, isLoading } = useQnaAssets();

    const { savingState: qnaSavingState, displayedQnaPairStores, qnaPairsJson } = qnaUiStore;
    const { savingState: synonymSavingState, displayedSynonymStores, SynonymsJson } = synonymUiStore;

    const isInEmptyState = (activeTab === 'qnas' && qnaPairsJson.length === 0) || (activeTab === 'synonyms' && SynonymsJson.length === 0);

    const { editSynonym, saveChanges } = useSynonymsActions(synonymUiStore);

    const [isMultiSelectMode, setIsMultiSelectMode] = React.useState(false);

    const unstructuredSources = items.reduce((acc, item) => (item.contentStructureKind === 'unstructured' ? acc + 1 : acc), 0);
    const structuredSources = items.reduce((acc, item) => (item.contentStructureKind === 'semistructured' ? acc + 1 : acc), 0);

    const recalculateSizeAllowance = (totalWidth: number) => {
        const newSidePaneMaxSize = totalWidth * 0.75;
        if (maxWidth !== newSidePaneMaxSize) {
            setMaxWidth(newSidePaneMaxSize);
        }
    };

    React.useEffect(() => {
        if (!uiStore.isSidebarCollapsed) {
            dispatcher.dispatch('setIsSidebarCollapsed', { isSidebarCollapsed: true });
        }
    }, []);

    React.useEffect(() => {
        if (rootElementRef.current) {
            const watcher = watchResize(rootElementRef.current, dim => {
                recalculateSizeAllowance(dim.width);
            });

            return () => watcher.unobserve();
        }
    });

    return (
        <Page pageName="QnaMakerEditPage" title={localize(messages.pageTitle)} reactToZoomLevel={false}>
            {isLoading ? (
                <Stack tokens={{ padding: '10px' }} styles={{ root: { width: '100%', minHeight: '50vh', justifyContent: 'center' } }}>
                    <Spinner size={SpinnerSize.large} label={localize(messages.loading)} style={{ transform: 'scale(1.5)' }} />
                </Stack>
            ) : (
                <>
                    <UnsavedChangesPrompt
                        isPageDirty={qnaSavingState === 'dirty' || synonymSavingState === 'dirty'}
                        promptPath={routeTable.qnaMaker.editPage()}
                    />
                    <Root ref={rootElementRef}>
                        <VerticalSplitPane
                            {...{
                                size: width,
                                minSize: 400,
                                primary: 'first',
                                defaultSize: 400,
                                maxSize: maxWidth,
                                onChange: setWidth,
                                onResizerDoubleClick: () => setWidth('400px')
                            }}>
                            <QnaMakerLeftSidePane
                                setWidth={setWidth}
                                activeTab={activeTab}
                                qnaUiStore={qnaUiStore}
                                synonymUiStore={synonymUiStore}
                                isCommandBarInExpandedState={Number(width) >= 600}
                                setMultiSelectMode={state => setIsMultiSelectMode(state)}
                            />

                            <Stack verticalFill grow tokens={{ padding: `0 0 ${isInEmptyState || isMultiSelectMode ? '0' : '30px'} 0` }}>
                                <HeaderContainer>
                                    <Stack.Item tokens={{ padding: '0 0 0 28px' }}>
                                        <EditableTitle title={localize(messages.pageTitle)} />

                                        {tracker.isRunning ? (
                                            <Shimmer
                                                shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                                                styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                                            />
                                        ) : (
                                            <LuisText>
                                                {localize(messages.sources, { unstructuredSources, structuredSources })} &nbsp;
                                                <Link
                                                    onClick={() =>
                                                        history.push(
                                                            routeTable.qnaMaker.manageSourcesPage(currentQnaMakerProjectStore.name)
                                                        )
                                                    }>
                                                    {localize(messages.viewSources)}
                                                </Link>
                                            </LuisText>
                                        )}
                                    </Stack.Item>
                                    <Stack.Item tokens={{ padding: '0 0 0 20px' }}>
                                        {activeTab === 'synonyms' && (
                                            <QnaMakerSynonymGroupRightCommandBar
                                                onSave={() => saveChanges(true)}
                                                isInEmptyState={SynonymsJson.length === 0}
                                                isInDirtyState={synonymSavingState === 'dirty'}
                                                onDiscard={() => synonymUiStore.resetSynonym()}
                                                areEditActionsDisabled={synonymSavingState === 'inProgress'}
                                                isEditSynonymOn={Boolean(synonymUiStore.synonymsBeingEditedId)}
                                                onEdit={() => synonymUiStore.setSynonymsBeingEditedId(synonymUiStore.selectedSynonymId)}
                                            />
                                        )}
                                    </Stack.Item>
                                </HeaderContainer>
                                {((qnaSavingState === 'dirty' && activeTab === 'qnas') ||
                                    (synonymSavingState === 'dirty' && activeTab === 'synonyms')) && (
                                    <Stack.Item>
                                        <MessageBar
                                            isMultiline={false}
                                            dismissButtonAriaLabel="Close"
                                            styles={{ root: { width: '100%' } }}
                                            messageBarType={MessageBarType.warning}>
                                            {localize(messages.unsavedChangesWarningMsg)}
                                        </MessageBar>
                                        <Announced aria-live="assertive" message={localize(messages.unsavedChangesWarningMsg)} />
                                    </Stack.Item>
                                )}
                                <Stack styles={{ root: { overflow: 'unset' } }} verticalFill>
                                    {activeTab === 'qnas' ? (
                                        <>
                                            {qnaPairsJson.length === 0 ? (
                                                <EmptyStateView message={<LuisText>{localize(messages.qnaEmptyStateMessage)}</LuisText>} />
                                            ) : (
                                                <>
                                                    {isMultiSelectMode ? (
                                                        <EmptyStateView
                                                            imageSrc={getAssetsUrl(`assets/images/logos/qnaMultiSelectLogo.svg`)}
                                                            message={
                                                                <LuisText fontWeight={600}>
                                                                    {localize(messages.multiSelectStateMessage)}
                                                                </LuisText>
                                                            }
                                                        />
                                                    ) : (
                                                        <>
                                                            {Boolean(qnaUiStore.selectedQnaId) && (
                                                                <QnaMakerQnaPairDetails
                                                                    qnaUiStore={qnaUiStore}
                                                                    qnaPairData={displayedQnaPairStores.find(
                                                                        qna => qna.id === qnaUiStore.selectedQnaId
                                                                    )}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {SynonymsJson.length === 0 ? (
                                                <EmptyStateView
                                                    message={<LuisText>{localize(messages.synonymEmptyStateMessage)}</LuisText>}
                                                />
                                            ) : (
                                                <>
                                                    {Boolean(synonymUiStore.selectedSynonymId) && (
                                                        <QnaMakerSynonymGroupDetails
                                                            isRemovingDisabled={synonymSavingState === 'inProgress'}
                                                            isInEditMode={Boolean(synonymUiStore.synonymsBeingEditedId)}
                                                            onEditModeExit={() => synonymUiStore.clearSynonymsBeingEditedId()}
                                                            onEditSynonyms={alterations =>
                                                                editSynonym(synonymUiStore.selectedSynonymId, { alterations })
                                                            }
                                                            synonymGroupData={displayedSynonymStores.find(
                                                                syn => syn.id === synonymUiStore.selectedSynonymId
                                                            )}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                        </VerticalSplitPane>
                    </Root>
                </>
            )}
        </Page>
    );
});
